import { useEffect, useMemo, useState } from 'react';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import type { FeeBreakdown } from '@features/booking/utils/calculateFee';
import { foreignCardUsdTotal } from '@features/booking/utils/foreignCardUsd';
import { fetchViewerCountry, peekViewerCountry, viewerCountrySettled } from '@features/booking/utils/viewerCountry';

export interface UsdView {
  /** USD a foreign card is charged for this deposit; null when there is none to state. */
  usdTotal: number | null;
  /** The visitor browses from outside the venue's country (unknown → false). */
  foreignViewer: boolean;
  /** A USD amount exists and the visitor's country is still being looked up. */
  pending: boolean;
}

function useViewerCountry(): { country: string | null; settled: boolean } {
  const [state, setState] = useState(() => ({ country: peekViewerCountry(), settled: viewerCountrySettled() }));

  useEffect(() => {
    let alive = true;
    void fetchViewerCountry().then((country) => { if (alive) setState({ country, settled: true }); });
    return () => { alive = false; };
  }, []);

  return state;
}

function isForeign(country: string | null, venueIso: string | null | undefined): boolean {
  const venueCountry = (venueIso ?? '').toUpperCase();
  return !!country && !!venueCountry && country !== venueCountry;
}

/** The visitor browses from outside the venue's country (unknown or not yet known → false). */
export function useViewerIsForeign(): boolean {
  const restaurant = useRestaurant();
  return isForeign(useViewerCountry().country, restaurant.isoCountryCode);
}

export function useForeignCardUsd(breakdown: FeeBreakdown): UsdView {
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { country, settled } = useViewerCountry();
  const usdTotal = useMemo(() => foreignCardUsdTotal(booking, restaurant, breakdown), [booking, restaurant, breakdown]);
  return {
    usdTotal,
    foreignViewer: isForeign(country, restaurant.isoCountryCode),
    pending: usdTotal != null && !settled,
  };
}
